import { useEffect, useState } from 'react'

import { type AuditHistoryParams, type BrokersPaginatedAuditList } from 'types/audit'
import { fetchWithHeaders } from 'utils'

interface UseAuditHistoryResult {
  data: BrokersPaginatedAuditList | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

export const useAuditHistory = (
  params: AuditHistoryParams | null
): UseAuditHistoryResult => {
  const [data, setData] = useState<BrokersPaginatedAuditList | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAuditHistory = async (fetchParams: AuditHistoryParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const searchParams = new URLSearchParams({
        auditable_type: fetchParams.auditable_type,
        auditable_id: fetchParams.auditable_id,
      })

      if (fetchParams.page) {
        searchParams.append('page[number]', fetchParams.page.toString())
      }

      if (fetchParams.items) {
        searchParams.append('page[size]', fetchParams.items.toString())
      }

      const response = await fetchWithHeaders(`/api/web/brokers/audits?${searchParams}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}`)
      }

      const auditData: BrokersPaginatedAuditList = await response.json()
      setData(auditData)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch audit history'
      setError(errorMessage)
      console.error('Error fetching audit history:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (params) {
      fetchAuditHistory(params)
    } else {
      setData(null)
      setError(null)
    }
  }, [params?.auditable_type, params?.auditable_id, params?.page, params?.items])

  const refetch = () => {
    if (params) {
      fetchAuditHistory(params)
    }
  }

  return { data, isLoading, error, refetch }
}
